/**
 * ReviewQueue — Lead Pass / Fail review gate (Foundation Brief §14).
 *
 * The executive editor's chair. Every chain — orchestrator-produced or
 * manually authored — stops at this gate before publishing. Pass moves it
 * forward (to teacher_review per §15). Fail sends it back to revision with
 * required notes. Nothing bypasses this gate.
 *
 * UI: queue list on the left, full chain preview on the right (script,
 * sources, era, contradiction state, spine context). Two buttons: Pass and
 * Fail. Fail requires notes ≥10 chars.
 */
import { useEffect, useState } from 'react';
import api from '../lib/api';
import { CheckCircle2, XCircle, FileText, Clock, ArrowRight, Loader2, AlertCircle } from 'lucide-react';

const STEP_KEYS = ['origin', 'change_a', 'change_b', 'contradiction', 'modern_pattern'];

export default function ReviewQueue() {
  const [queue, setQueue] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [selected, setSelected] = useState(null);
  const [chainDetail, setChainDetail] = useState(null);
  const [failNotes, setFailNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/review/queue?status=${filter}`);
      setQueue(r.data || []);
    } catch (e) {
      setMessage(e.response?.data?.detail || 'Failed to load queue');
    }
    setLoading(false);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filter]);

  const openItem = async (item) => {
    setSelected(item);
    setFailNotes('');
    setMessage('');
    try {
      const r = await api.get(`/lessons/${item.chain_id}`);
      setChainDetail(r.data);
    } catch {
      setChainDetail(null);
    }
  };

  const doPass = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post(`/admin/review/${selected.id}/pass`);
      setMessage('Passed. Chain moved to teacher review.');
      setSelected(null); setChainDetail(null);
      reload();
    } catch (e) {
      setMessage(e.response?.data?.detail || 'Pass failed');
    }
    setBusy(false);
  };
  const doFail = async () => {
    if (!selected) return;
    if (failNotes.trim().length < 10) {
      setMessage('Fail notes required (min 10 chars). Tell the pipeline what to fix.');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/review/${selected.id}/fail`, { fail_notes: failNotes.trim() });
      setMessage('Failed. Sent back to revision with your notes.');
      setSelected(null); setChainDetail(null);
      reload();
    } catch (e) {
      setMessage(e.response?.data?.detail || 'Fail submit failed');
    }
    setBusy(false);
  };

  return (
    <div className="p-4 md:p-6" data-testid="review-queue">
      <div className="mb-5">
        <h2 className="font-historical text-[#D4A84A] text-3xl md:text-4xl lg:text-5xl font-light tracking-tight">
          Lead Review Queue
        </h2>
        <p className="font-hud text-[10px] md:text-xs text-white/80 tracking-wider mt-1">
          PASS / FAIL · NOTHING PUBLISHES WITHOUT YOUR APPROVAL
        </p>
      </div>

      <div className="flex items-center gap-2 mb-4">
        {['pending', 'passed', 'failed', 'all'].map((s) => (
          <button
            key={s}
            data-testid={`review-filter-${s}`}
            onClick={() => { setFilter(s); setSelected(null); setChainDetail(null); }}
            className={`px-3 py-1.5 font-hud text-[10px] md:text-xs tracking-widest uppercase rounded transition-colors ${
              filter === s ? 'text-[#D4A84A] border border-[#D4A84A]/40 bg-[#D4A84A]/5' : 'text-white/65 border border-transparent hover:text-[#D4A84A]'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {message && (
        <div className="mb-4 px-3 py-2 border border-[#2F8A92]/25 bg-[#2F8A92]/05 rounded font-mono text-xs md:text-sm text-white/85 flex items-center gap-2">
          <AlertCircle size={13} className="text-[#2F8A92]" /> {message}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Queue list */}
        <div className="border border-[#2F8A92]/15 rounded">
          <div className="px-3 py-2 border-b border-[#2F8A92]/10 font-hud text-[10px] md:text-xs text-[#2F8A92] tracking-widest uppercase">
            Queue ({queue.length})
          </div>
          {loading ? (
            <div className="p-6 text-center"><Loader2 size={18} className="animate-spin text-[#2F8A92] mx-auto" /></div>
          ) : queue.length === 0 ? (
            <div className="p-6 font-mono text-xs md:text-sm text-white/65 text-center">No items {filter !== 'all' ? `in ${filter}` : ''}.</div>
          ) : (
            <ul className="divide-y divide-[#2F8A92]/8 max-h-[600px] overflow-y-auto">
              {queue.map((item) => (
                <li key={item.id}>
                  <button
                    data-testid={`review-item-${item.id}`}
                    onClick={() => openItem(item)}
                    className={`w-full text-left px-3 py-2.5 hover:bg-[#2F8A92]/5 transition-colors ${
                      selected?.id === item.id ? 'bg-[#2F8A92]/8' : ''
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <FileText size={13} className="text-[#D4A84A] mt-1 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-sm text-white truncate">{item.chain_preview?.subject || item.chain_subject}</div>
                        <div className="flex items-center gap-3 mt-1 font-mono text-[10px] md:text-xs text-white/55">
                          <span className="inline-flex items-center gap-1">
                            <Clock size={10} /> {new Date(item.submitted_at).toLocaleString()}
                          </span>
                          <span className="font-hud tracking-widest uppercase text-[9px]" style={{
                            color: item.status === 'pending' ? '#D4A84A' : item.status === 'passed' ? '#2F8A92' : '#FF6B6B',
                          }}>
                            {item.status} · {item.submission_kind}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Chain preview */}
        <div className="border border-[#D4A84A]/15 rounded">
          {!selected ? (
            <div className="p-8 text-center font-mono text-sm md:text-base text-white/65">
              Select a queue item to preview the chain as users will see it.
            </div>
          ) : (
            <div className="p-4 md:p-5 max-h-[600px] overflow-y-auto">
              <h3 className="font-historical text-[#D4A84A] text-xl md:text-2xl font-light tracking-tight">
                {chainDetail?.chain?.subject || selected.chain_preview?.subject}
              </h3>
              <p className="font-mono text-xs md:text-sm text-white/65 mt-1">
                {chainDetail?.chain?.description || selected.chain_preview?.description}
              </p>

              {chainDetail?.chain && (
                <div className="mt-4 space-y-4">
                  {STEP_KEYS.map((sk) => {
                    const step = chainDetail.chain[sk];
                    if (!step) return null;
                    return (
                      <div key={sk} className="border-l-2 border-[#2F8A92]/30 pl-3">
                        <div className="font-hud text-[9px] md:text-[10px] tracking-widest uppercase text-[#2F8A92]">
                          {sk.replace('_', ' ')}{step.year ? ` · ${step.year < 0 ? `${Math.abs(step.year)} BCE` : `${step.year} CE`}` : ''}{step.location ? ` · ${step.location}` : ''}
                        </div>
                        <div className="font-historical text-base md:text-lg text-[#e8d5a3] mt-0.5">{step.title}</div>
                        <p className="font-mono text-xs md:text-sm text-white/85 mt-1 leading-relaxed">{step.content}</p>
                        {step.source && (
                          <p className="font-mono text-[10px] md:text-xs text-white/55 mt-1 italic">Source: {step.source}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {selected.status === 'pending' && (
                <div className="mt-6 pt-4 border-t border-[#D4A84A]/15 space-y-3">
                  <div className="flex flex-wrap gap-2">
                    <button
                      data-testid="review-pass-btn"
                      onClick={doPass}
                      disabled={busy}
                      className="font-hud text-xs md:text-sm tracking-widest text-[#0A0A0A] bg-[#2F8A92] px-4 py-2 inline-flex items-center gap-2 hover:bg-[#66FAFF] disabled:opacity-50"
                    >
                      {busy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                      PASS &mdash; PROMOTE TO TEACHER REVIEW
                    </button>
                  </div>
                  <textarea
                    data-testid="review-fail-notes"
                    value={failNotes}
                    onChange={(e) => setFailNotes(e.target.value)}
                    placeholder="If failing: tell the pipeline exactly what needs to change. Min 10 chars."
                    rows={3}
                    className="w-full bg-[#0A0A0A] border border-[#FF6B6B]/25 rounded px-3 py-2 font-mono text-xs md:text-sm text-white/85 outline-none focus:border-[#FF6B6B]/55"
                  />
                  <button
                    data-testid="review-fail-btn"
                    onClick={doFail}
                    disabled={busy || failNotes.trim().length < 10}
                    className="font-hud text-xs md:text-sm tracking-widest text-[#FF6B6B] border border-[#FF6B6B]/40 px-4 py-2 inline-flex items-center gap-2 hover:bg-[#FF6B6B]/8 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <XCircle size={13} /> FAIL &mdash; SEND BACK TO REVISION
                  </button>
                </div>
              )}

              {selected.status === 'failed' && selected.fail_notes && (
                <div className="mt-6 pt-4 border-t border-[#FF6B6B]/20">
                  <div className="font-hud text-[10px] md:text-xs tracking-widest text-[#FF6B6B] uppercase mb-1">Fail notes</div>
                  <p className="font-mono text-xs md:text-sm text-white/85 leading-relaxed">{selected.fail_notes}</p>
                </div>
              )}

              {selected.status === 'passed' && (
                <div className="mt-6 pt-4 border-t border-[#2F8A92]/20 font-mono text-xs md:text-sm text-white/65 inline-flex items-center gap-2">
                  <CheckCircle2 size={13} className="text-[#2F8A92]" /> Passed Lead review &mdash; promoted to teacher review
                  <ArrowRight size={13} className="text-white/35" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
